/**
 * Admin user permissions breakdown (GET /users/:id/permissions-detail).
 */

import { authenticatedProApiFetch } from "./pro-api.client";

export interface PermissionDetailItem {
  id: string;
  name: string;
  description?: string | null;
  resource?: string | null;
  action?: string | null;
  source: "role" | "direct";
  roleName?: string;
}

export interface OrganizationPermissionGroup {
  organizationId: string;
  organizationName: string;
  roleId?: string | null;
  roleName?: string | null;
  permissions: PermissionDetailItem[];
}

export interface RolePermissionGroup {
  roleId: string;
  roleName: string;
  permissions: PermissionDetailItem[];
}

export interface LionSmartGlobalPermissions {
  direct: PermissionDetailItem[];
  fromRoles: RolePermissionGroup[];
}

export interface UserPermissionsDetail {
  organizations: OrganizationPermissionGroup[];
  /** @deprecated Prefer `lionSmartGlobal.fromRoles`. */
  legacySmart: RolePermissionGroup[];
  lionSmartGlobal: LionSmartGlobalPermissions;
  lionProGlobal: {
    direct: PermissionDetailItem[];
    fromRoles: RolePermissionGroup[];
  };
}

const LION_PRO_PLATFORM_PERMISSION_NAMES = new Set([
  "user:read",
  "user:undelete",
  "user:delete",
  "user:hard-delete",
  "user:update",
  "user:masquerade",
  "role:read",
  "role:create",
  "role:update",
  "role:delete",
  "permission:read",
  "permission:create",
  "permission:update",
  "permission:delete",
  "organization:list",
  "organization:create",
  "organization:update",
  "organization:delete",
]);

export function getSmartRoleGroups(
  detail: UserPermissionsDetail,
): RolePermissionGroup[] {
  return detail.lionSmartGlobal.fromRoles.length > 0
    ? detail.lionSmartGlobal.fromRoles
    : detail.legacySmart;
}

export function getSmartDirectPermissions(
  detail: UserPermissionsDetail,
): PermissionDetailItem[] {
  return detail.lionSmartGlobal.direct;
}

/** All direct grant IDs (Lion Pro platform + Lion Smart). */
export function getAllDirectPermissionIds(detail: UserPermissionsDetail): string[] {
  return [
    ...detail.lionProGlobal.direct,
    ...getSmartDirectPermissions(detail),
  ]
    .map((p) => p.id)
    .filter(Boolean);
}

function normalizePermissionItem(
  raw: Record<string, unknown>,
): PermissionDetailItem {
  return {
    id: String(raw.id ?? ""),
    name: String(raw.name ?? ""),
    description:
      raw.description != null ? String(raw.description) : null,
    resource:
      raw.resource != null
        ? String(raw.resource)
        : raw.resource_name != null
          ? String(raw.resource_name)
          : null,
    action:
      raw.action != null
        ? String(raw.action)
        : raw.action_name != null
          ? String(raw.action_name)
          : null,
    source: raw.source === "direct" ? "direct" : "role",
    roleName:
      raw.roleName != null
        ? String(raw.roleName)
        : raw.role_name != null
          ? String(raw.role_name)
          : undefined,
  };
}

function normalizeRoleGroup(raw: Record<string, unknown>): RolePermissionGroup {
  const perms = raw.permissions;
  return {
    roleId: String(raw.roleId ?? raw.role_id ?? ""),
    roleName: String(raw.roleName ?? raw.role_name ?? ""),
    permissions: Array.isArray(perms)
      ? perms.map((p) =>
          normalizePermissionItem(p as Record<string, unknown>),
        )
      : [],
  };
}

export function normalizeUserPermissionsDetail(
  raw: Record<string, unknown>,
): UserPermissionsDetail {
  const orgs = raw.organizations;
  const legacy = raw.legacySmart ?? raw.legacy_smart;
  const lionSmartRaw = (raw.lionSmartGlobal ?? raw.lion_smart_global) as
    | Record<string, unknown>
    | undefined;
  const lionPro = (raw.lionProGlobal ?? raw.lion_pro_global) as
    | Record<string, unknown>
    | undefined;
  const lionProFromRolesRaw = lionPro?.fromRoles ?? lionPro?.from_roles;
  const legacyFromRoles = Array.isArray(legacy)
    ? legacy.map((g) => normalizeRoleGroup(g as Record<string, unknown>))
    : [];

  const lionSmartFromRolesRaw =
    lionSmartRaw?.fromRoles ?? lionSmartRaw?.from_roles;
  const smartFromRoles = Array.isArray(lionSmartFromRolesRaw)
    ? lionSmartFromRolesRaw.map((g: unknown) =>
        normalizeRoleGroup(g as Record<string, unknown>),
      )
    : legacyFromRoles;

  let lionSmartDirect = Array.isArray(lionSmartRaw?.direct)
    ? lionSmartRaw.direct.map((p) =>
        normalizePermissionItem(p as Record<string, unknown>),
      )
    : [];

  let lionProDirect = Array.isArray(lionPro?.direct)
    ? lionPro.direct.map((p) =>
        normalizePermissionItem(p as Record<string, unknown>),
      )
    : [];

  if (!lionSmartRaw?.direct && lionProDirect.length > 0) {
    const smartDirect: PermissionDetailItem[] = [];
    const proOnly: PermissionDetailItem[] = [];
    for (const item of lionProDirect) {
      if (
        !LION_PRO_PLATFORM_PERMISSION_NAMES.has(item.name) &&
        !item.name.startsWith("org:")
      ) {
        smartDirect.push(item);
      } else if (LION_PRO_PLATFORM_PERMISSION_NAMES.has(item.name)) {
        proOnly.push(item);
      }
    }
    lionSmartDirect = smartDirect;
    lionProDirect = proOnly;
  }

  return {
    organizations: Array.isArray(orgs)
      ? orgs.map((o) => {
          const row = o as Record<string, unknown>;
          const perms = row.permissions;
          return {
            organizationId: String(
              row.organizationId ?? row.organization_id ?? "",
            ),
            organizationName: String(
              row.organizationName ?? row.organization_name ?? "",
            ),
            roleId:
              row.roleId != null
                ? String(row.roleId)
                : row.role_id != null
                  ? String(row.role_id)
                  : null,
            roleName:
              row.roleName != null
                ? String(row.roleName)
                : row.role_name != null
                  ? String(row.role_name)
                  : null,
            permissions: Array.isArray(perms)
              ? perms.map((p) =>
                  normalizePermissionItem(p as Record<string, unknown>),
                )
              : [],
          };
        })
      : [],
    legacySmart: legacyFromRoles,
    lionSmartGlobal: {
      direct: lionSmartDirect,
      fromRoles: smartFromRoles,
    },
    lionProGlobal: {
      direct: lionProDirect,
      fromRoles: Array.isArray(lionProFromRolesRaw)
        ? lionProFromRolesRaw.map((g: unknown) =>
            normalizeRoleGroup(g as Record<string, unknown>),
          )
        : [],
    },
  };
}

export async function fetchUserPermissionsDetail(
  accessToken: string,
  userId: string,
): Promise<UserPermissionsDetail> {
  const raw = await authenticatedProApiFetch<Record<string, unknown>>(
    `/users/${userId}/permissions-detail`,
    accessToken,
    { method: "GET" },
  );
  return normalizeUserPermissionsDetail(raw);
}
